/*
 * WR-024 Horizon view plan logic; pure; the client recompute uses the same scheduler as the server, so the two must agree
 */

import { schedule, weekStart, weekFriday } from './horizonScheduler';

/**
 * Rows = entries + milestones, sorted by order (entry before milestone on tie), then array position.
 */
function sequence(horizon) {
  const entries = (horizon.entries || []).map((e, i) => ({ kind: 'entry', item: e, id: String(e._id || e.id), rank: i }));
  const milestones = (horizon.milestones || []).map((m, i) => ({ kind: 'milestone', item: m, id: String(m._id || m.id), rank: i }));
  return entries.concat(milestones).sort((a, b) =>
    (a.item.order - b.item.order)
    || ((a.kind === 'entry' ? 0 : 1) - (b.kind === 'entry' ? 0 : 1))
    || (a.rank - b.rank));
}

/**
 * Internal helper: rows in processing order, each EXACTLY { kind: 'entry'|'milestone', id, row }.
 */
function planRows(h) {
  const entries = (h.entries || []).map((e, i) => ({ kind: 'entry', id: String(e._id || e.id), row: e, rank: i }));
  const milestones = (h.milestones || []).map((m, i) => ({ kind: 'milestone', id: String(m._id || m.id), row: m, rank: i }));
  return entries.concat(milestones).sort((a, b) =>
    (a.row.order - b.row.order)
    || ((a.kind === 'entry' ? 0 : 1) - (b.kind === 'entry' ? 0 : 1))
    || (a.rank - b.rank));
}

/**
 * Internal helper: index i in planRows(h) of the row with this id, or -1.
 */
function find(h, id) {
  const seq = planRows(h);
  for (let i = 0; i < seq.length; i += 1) {
    if (seq[i].id === String(id)) {
      return i;
    }
  }
  return -1;
}

/**
 * Internal helper: label of a row used in sentences.
 */
function label(item) {
  if (item.kind === 'milestone') {
    return item.row.name;
  }
  const title = item.row.snapshot.bstId || item.row.snapshot.title || 'a removed task';
  if (item.row.taskState) {
    return title + ' (' + item.row.taskState + ')';
  }
  return title;
}

/**
 * Internal helper: analyse(h, id) → null for unknown id, else { me, deps, lanes }.
 */
function analyse(h, id) {
  const seq = planRows(h);
  const i = find(h, id);
  if (i < 0) {
    return null;
  }
  const me = seq[i];
  const before = seq.slice(0, i);

  const deps = [];
  for (const ref of me.row.after || []) {
    for (const item of before) {
      if (item.id === String(ref)) {
        deps.push(item);
        break;
      }
    }
  }

  const lanes = [];
  if (me.kind === 'entry') {
    for (const laneId of me.row.lanes || []) {
      const lane = h.lanes ? h.lanes.find(l => String(l._id || l.id) === String(laneId)) : null;
      if (!lane || lane.capacity !== 'serial') {
        continue;
      }
      let blocker = null;
      for (const item of before) {
        if (item.kind === 'entry' && (item.row.lanes || []).includes(String(laneId)) && item.row.end !== null) {
          if (blocker === null || item.row.end >= blocker.row.end) {
            blocker = item;
          }
        }
      }
      if (blocker) {
        lanes.push({ lane, blocker });
      }
    }
  }

  return { me, deps, lanes };
}

/**
 * toSchedulerInput: map Horizon API shape to scheduler input shape.
 */
export function toSchedulerInput(h) {
  return {
    lanes: (h.lanes || []).map(l => ({
      id: String(l._id || l.id),
      capacity: l.capacity,
    })),
    entries: (h.entries || []).map(e => ({
      id: String(e._id || e.id),
      lanes: (e.lanes || []).map(String),
      duration: e.duration,
      after: (e.after || []).map(String),
      notBefore: e.notBefore,
      pinnedStart: e.pinnedStart,
      order: e.order,
      taskState: e.taskState,
    })),
    milestones: (h.milestones || []).map(m => ({
      id: String(m._id || m.id),
      week: m.week,
      after: (m.after || []).map(String),
      order: m.order,
    })),
  };
}

/**
 * recompute: return a new Horizon with all weeks/dates recomputed via the scheduler.
 */
export function recompute(h) {
  const hCopy = JSON.parse(JSON.stringify(h));
  const result = schedule(toSchedulerInput(hCopy));

  for (const entryResult of result.entries) {
    const entry = hCopy.entries.find(e => String(e._id || e.id) === entryResult.id);
    if (entry) {
      entry.start = entryResult.start;
      entry.end = entryResult.end;
      entry.startDate = weekStart(hCopy.anchorDate, entryResult.start).toISOString().slice(0, 10);
      entry.endDate = weekFriday(hCopy.anchorDate, entryResult.end).toISOString().slice(0, 10);
    }
  }
  for (const milestoneResult of result.milestones) {
    const milestone = hCopy.milestones.find(m => String(m._id || m.id) === milestoneResult.id);
    if (milestone) {
      milestone.start = milestoneResult.week;
      milestone.end = milestoneResult.week;
      milestone.date = weekStart(hCopy.anchorDate, milestoneResult.week).toISOString().slice(0, 10);
    }
  }
  hCopy.weeks = Math.max(hCopy.weeks, result.lastWeek);

  // Sort entries and milestones by order (entries before milestones on tie)
  hCopy.entries.sort((a, b) =>
    (a.order - b.order) || ((a._id || a.id) < (b._id || b.id) ? -1 : 1)
  );
  hCopy.milestones.sort((a, b) =>
    (a.order - b.order) || ((a._id || a.id) < (b._id || b.id) ? -1 : 1)
  );

  return hCopy;
}

/**
 * scheduleMismatches: compare stored schedule with fresh client schedule.
 */
export function scheduleMismatches(h) {
  const result = schedule(toSchedulerInput(h));
  const entriesMap = new Map(result.entries.map(e => [e.id, { start: e.start, end: e.end }]));
  const milestonesMap = new Map(result.milestones.map(m => [m.id, m.week]));

  const mismatches = [];

  for (const entry of h.entries || []) {
    const client = entriesMap.get(String(entry._id || entry.id));
    if (client && (entry.start !== client.start || entry.end !== client.end)) {
      mismatches.push({
        id: String(entry._id || entry.id),
        kind: 'entry',
        server: [entry.start, entry.end],
        client: [client.start, client.end],
      });
    }
  }

  for (const milestone of h.milestones || []) {
    const clientWeek = milestonesMap.get(String(milestone._id || milestone.id));
    if (clientWeek !== undefined && (milestone.start !== clientWeek || milestone.end !== clientWeek)) {
      mismatches.push({
        id: String(milestone._id || milestone.id),
        kind: 'milestone',
        server: [milestone.start, milestone.end],
        client: [clientWeek, clientWeek],
      });
    }
  }

  return mismatches;
}

/**
 * moveRow: return all row ids in order with movedId re-inserted at toIndex.
 */
export function moveRow(h, movedId, toIndex) {
  const allIds = sequence(h).map(x => x.id);
  const idx = allIds.indexOf(movedId);
  if (idx === -1) {
    return allIds;
  }
  const newIds = allIds.filter(id => id !== movedId);
  newIds.splice(toIndex, 0, movedId);
  return newIds;
}

/**
 * applyOrder: set each row's order to its index + 1 in orderedIds and recompute.
 */
export function applyOrder(h, orderedIds) {
  const hCopy = JSON.parse(JSON.stringify(h));
  const idToIndex = new Map(orderedIds.map((id, i) => [String(id), i]));
  const rows = hCopy.entries.concat(hCopy.milestones);
  for (const row of rows) {
    const idx = idToIndex.get(String(row._id || row.id));
    if (idx !== undefined) {
      row.order = idx + 1;
    }
  }
  return recompute(hCopy);
}

/**
 * rowState: determine the status state of a row given today's week.
 */
export function rowState(entry, todayWeek) {
  if (entry.taskState === 'archived') {
    return 'archived';
  }
  if (entry.taskState === 'deleted') {
    return 'deleted';
  }
  if (entry.snapshot && entry.snapshot.status === 'done') {
    return 'done';
  }
  if (entry.end !== null && entry.end !== undefined && todayWeek > entry.end) {
    return 'overdue';
  }
  if (entry.snapshot && entry.snapshot.status === 'inprogress') {
    return 'inprogress';
  }
  return 'planned';
}

/**
 * statusChip: return the chip object for a given state, or null.
 */
export function statusChip(state) {
  if (state === 'inprogress') {
    return { label: 'IN PROGRESS', tone: 'steel' };
  }
  if (state === 'done') {
    return { label: 'DONE', tone: 'ok' };
  }
  if (state === 'overdue') {
    return { label: 'OVERDUE', tone: 'critical' };
  }
  if (state === 'archived') {
    return { label: 'ARCHIVED', tone: 'muted' };
  }
  if (state === 'deleted') {
    return { label: 'REMOVED', tone: 'muted' };
  }
  return null;
}

/**
 * defaultLaneFor: return the lane id whose member matches the task's first assignee.
 */
export function defaultLaneFor(h, task) {
  if (!task.assignedTo || task.assignedTo.length === 0) {
    return null;
  }
  const firstAssignee = task.assignedTo[0];
  const assigneeId = typeof firstAssignee === 'object' ? firstAssignee._id : firstAssignee;

  for (const lane of h.lanes || []) {
    const laneMember = lane.member;
    if (laneMember !== null && laneMember !== undefined) {
      const laneMemberId = typeof laneMember === 'object' ? laneMember._id : laneMember;
      if (String(laneMemberId) === String(assigneeId)) {
        return String(lane._id || lane.id);
      }
    }
  }
  return null;
}

/**
 * filterPickerTasks: filter a task list by picker filters and excluded ids.
 */
export function filterPickerTasks(tasks, filters, excludeTaskIds) {
  const excludeSet = Array.isArray(excludeTaskIds) ? new Set(excludeTaskIds) : excludeTaskIds;
  const result = [];

  for (const task of tasks || []) {
    // Skip archived
    if (task.archived) {
      continue;
    }
    // Skip excluded
    if (excludeSet && excludeSet.has(task._id)) {
      continue;
    }

    // Apply filters (all must match if set)
  let match = true;
    const query = filters.query ? filters.query.trim().toLowerCase() : null;
    const title = (task.title || '').toLowerCase();
    const bstId = (task.bstId || '').toLowerCase();
    if (query && !title.includes(query) && !bstId.includes(query)) {
      match = false;
    }

    if (match && filters.columnId && task.columnId !== filters.columnId) {
      match = false;
    }

    if (match && filters.status && task.status !== filters.status) {
      match = false;
    }

    if (match && filters.assigneeId) {
      if (filters.assigneeId === 'unassigned') {
        if (task.assignedTo && task.assignedTo.length > 0) {
          match = false;
        }
      } else {
        const assigneeId = filters.assigneeId;
        const hasAssignee = (task.assignedTo || []).some(a => {
          const id = typeof a === 'object' ? a._id : a;
          return String(id) === String(assigneeId);
        });
        if (!hasAssignee) {
          match = false;
        }
      }
    }

    if (match && filters.tagId) {
      const tagId = filters.tagId;
      const hasTag = (task.tags || []).some(t => {
        const id = typeof t === 'object' ? t._id : t;
        return String(id) === String(tagId);
      });
      if (!hasTag) {
        match = false;
      }
    }

    if (match) {
      result.push(task);
    }
  }

  return result;
}

/**
 * placementsByTask: map task id → list of placements in list order.
 */
export function placementsByTask(list) {
  const map = new Map();
  for (const horizon of list || []) {
    const entries = horizon.entries || [];
    for (const entry of entries) {
      const taskId = entry.task;
      if (!map.has(taskId)) {
        map.set(taskId, []);
      }
      map.get(taskId).push({
        horizonId: String(horizon._id),
        horizonName: horizon.name,
        entryId: String(entry._id),
        start: entry.start,
        end: entry.end,
      });
    }
  }
  return map;
}

/**
 * predecessorsOf(h, id): return array of predecessor row ids.
 */
export function predecessorsOf(h, id) {
  const a = analyse(h, id);
  if (!a) {
    return [];
  }
  const out = [];
  for (const d of a.deps) {
    if (!out.includes(d.id)) {
      out.push(d.id);
    }
  }
  for (const l of a.lanes) {
    if (!out.includes(l.blocker.id)) {
      out.push(l.blocker.id);
    }
  }
  return out;
}

/**
 * reasonFor(h, id): return explanation string for why a row starts when it does.
 */
export function reasonFor(h, id) {
  const a = analyse(h, id);
  if (!a) {
    return '';
  }
  if (a.me.kind === 'milestone' && a.me.row.week != null) {
    return 'Fixed at W' + a.me.row.week;
  }
  if (a.me.kind === 'entry' && a.me.row.pinnedStart != null) {
    return 'Pinned to start in W' + a.me.row.pinnedStart;
  }
  const c = [];
  for (const d of a.deps) {
    const release = d.kind === 'milestone' ? d.row.start : d.row.end + 1;
    c.push({ release, text: 'for ' + label(d), nb: false });
  }
  const depIds = new Set(a.deps.map(d => d.id));
  for (const l of a.lanes) {
    if (!depIds.has(l.blocker.id)) {
      c.push({ release: l.blocker.row.end + 1, text: 'for ' + l.lane.name + ' to clear ' + label(l.blocker), nb: false });
    }
  }
  if (a.me.kind === 'entry' && a.me.row.notBefore != null) {
    c.push({ release: a.me.row.notBefore, text: 'not before W' + a.me.row.notBefore, nb: true });
  }
  const keep = c.filter(x => x.release > 1).sort((x, y) => x.release - y.release).slice(-2);
  if (keep.length === 0) {
    return 'Nothing ahead of it — starts W' + a.me.row.start;
  }
  const parts = keep.map((x, i) => i === 0
    ? (x.nb ? 'Not before W' + x.release : 'Waits ' + x.text)
    : ', then ' + x.text);
  return parts.join('');
}

/**
 * footerLine(h, id): return footer line string for timeline footer.
 */
export function footerLine(h, id) {
  const r = reasonFor(h, id);
  if (r === '') {
    return '';
  }
  const a = analyse(h, id);
  const name = a.me.kind === 'milestone' ? a.me.row.name : (a.me.row.snapshot.bstId || a.me.row.snapshot.title);
  let line = name + ' · ' + r[0].toLowerCase() + r.slice(1);
  if (r.startsWith('Waits for') || r.startsWith('Not before')) {
    line += a.me.kind === 'milestone' ? ' → W' + a.me.row.start : ' → starts W' + a.me.row.start;
  }
  return line;
}

/**
 * buildRows(h, collapsed): return table rows with section headers, collapsed Set of section keys.
 */
export function buildRows(h, collapsed) {
  const out = [];
  const headers = [];
  let header = null;
  let prev = undefined;
  let first = true;
  let n = 0;
  for (const item of planRows(h)) {
    n += 1;
    const sec = item.row.section || '';
    if ((first && sec !== '') || (!first && sec !== prev)) {
      header = { kind: 'section', key: sec, label: sec || 'No section', meta: '', collapsed: collapsed.has(sec), rows: [] };
      out.push(header);
      headers.push(header);
    }
    first = false;
    prev = sec;
    if (header) {
      header.rows.push(item);
    }
    if (!(header && header.collapsed)) {
      out.push({ kind: item.kind, id: item.id, n, row: item.row });
    }
  }
  for (const hd of headers) {
    const entries = hd.rows.filter(x => x.kind === 'entry');
    const ms = hd.rows.length - entries.length;
    const laneIds = new Set();
    for (const e of entries) {
      for (const laneId of e.row.lanes || []) {
        laneIds.add(String(laneId));
      }
    }
    const parts = [];
    if (entries.length) {
      parts.push(entries.length + (entries.length === 1 ? ' TASK' : ' TASKS'));
    }
    if (laneIds.size) {
      parts.push(laneIds.size + (laneIds.size === 1 ? ' LANE' : ' LANES'));
    }
    if (ms) {
      parts.push(ms + (ms === 1 ? ' MILESTONE' : ' MILESTONES'));
    }
    hd.meta = parts.join(' · ');
    delete hd.rows;
  }
  return out;
}

/**
 * afterLabel(h, id): return label for the After column.
 */
export function afterLabel(h, id) {
  const seq = planRows(h);
  const i = find(h, id);
  if (i < 0) {
    return '';
  }
  const refs = (seq[i].row.after || []).filter(ref => seq.some(x => x.id === String(ref)));
  if (refs.length === 0) {
    return '';
  }
  const j = seq.findIndex(x => x.id === String(refs[0]));
  const f = seq[j];
  const first = f.kind === 'milestone' ? 'M' + (j + 1) : (f.row.snapshot.bstId || '—').replace(/^BST-/, '');
  return refs.length > 1 ? first + ' +' + (refs.length - 1) : first;
}
